import React, { createContext, useState, useEffect, useContext, useRef } from "react";
import {
  auth,
  refreshAuthCookie,
  clearServerAuthCookie,
  isEmailVerifiedForSession,
} from "../config/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { authLog } from "../utils/authDebugLog";

const PROACTIVE_REFRESH_INTERVAL_MS = 50 * 60 * 1000; // 50 minutes
const SESSION_SYNC_MAX_ATTEMPTS = 8;
const SESSION_SYNC_BASE_DELAY_MS = 100;

/** Abort stale sync work when a newer onAuthStateChanged has started (fixes login → immediate bounce). */
function createSyncGuard(syncGenerationRef, myGeneration) {
  return () => syncGenerationRef.current === myGeneration;
}

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

async function parseJsonSafe(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

/**
 * Aligns React auth state with the server session cookie.
 *
 * Handles:
 * - Stale cookie after DB reset (JWT valid, user row gone → HTTP 404): clear cookie + refresh.
 * - Signup race: Firebase user exists before /api/auth/register finishes → retry with backoff.
 * - Login race: cookie not readable immediately after set-cookie → retry refresh + check.
 */
async function syncServerSession(user, setCurrentUser, shouldApply) {
  if (!user) {
    if (!shouldApply()) return;
    authLog("sync:noFirebaseUser");
    setCurrentUser(null);
    return;
  }

  // Server session is only issued for verified emails. Use reload + token claims (same as set-cookie);
  // user.emailVerified alone can be stale and wrongly clear a valid session.
  const verified = await isEmailVerifiedForSession(user);
  if (!shouldApply()) return;

  if (!verified) {
    authLog("sync:skipUnverified", {
      uidTail: user.uid?.slice(-8),
    });
    if (!shouldApply()) return;
    await clearServerAuthCookie();
    if (!shouldApply()) return;
    setCurrentUser(null);
    return;
  }

  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  authLog("sync:start", { uidTail: user.uid?.slice(-8) });

  // Align HTTP cookie with Firebase before /check (covers race with set-cookie + parallel listeners).
  const preflight = await refreshAuthCookie();
  authLog("sync:preflightRefresh", { ok: preflight.ok, needsVerification: !!preflight.needsVerification });
  if (!shouldApply()) return;
  if (preflight.needsVerification) {
    setCurrentUser(null);
    return;
  }

  const fetchCheck = () =>
    fetch("/api/auth/check", { credentials: "include", cache: "no-store" });

  for (let attempt = 0; attempt < SESSION_SYNC_MAX_ATTEMPTS; attempt++) {
    if (!shouldApply()) return;

    const response = await fetchCheck();
    const data = await parseJsonSafe(response);

    if (data.success && data.authenticated) {
      authLog("sync:checkOk", { attempt });
      if (!shouldApply()) return;
      setCurrentUser(user);
      return;
    }

    if (response.status === 403 && data.requiresEmailVerification) {
      authLog("sync:check403Verification", { attempt });
      const r = await refreshAuthCookie();
      if (!shouldApply()) return;
      if (r.ok) {
        const retry = await fetchCheck();
        const retryData = await parseJsonSafe(retry);
        if (retryData.success && retryData.authenticated) {
          authLog("sync:checkOkAfter403Refresh", { attempt });
          if (!shouldApply()) return;
          setCurrentUser(user);
          return;
        }
      }
      if (!shouldApply()) return;
      setCurrentUser(null);
      return;
    }

    const errLower = String(data.error || "").toLowerCase();
    const looksLikeUserMissing =
      response.status === 404 &&
      (errLower.includes("not found") || errLower.includes("user not found"));

    if (looksLikeUserMissing) {
      authLog("sync:check404UserMissing", { attempt });
      if (!shouldApply()) return;
      await clearServerAuthCookie();
      const r = await refreshAuthCookie();
      authLog("sync:after404Refresh", {
        attempt,
        refreshOk: r.ok,
        needsVerification: !!r.needsVerification,
      });
      if (!shouldApply()) return;
      if (r.needsVerification) {
        setCurrentUser(null);
        return;
      }
      if (r.ok) {
        await wait(SESSION_SYNC_BASE_DELAY_MS + attempt * 120);
        continue;
      }
      await wait(SESSION_SYNC_BASE_DELAY_MS + attempt * 120);
      continue;
    }

    const needsRefresh =
      response.status === 401 &&
      (data.requiresAuth === true ||
        errLower.includes("expired") ||
        errLower.includes("no authentication token") ||
        errLower.includes("invalid"));

    if (needsRefresh) {
      authLog("sync:check401Refresh", {
        attempt,
        err: data.error || "",
      });
      const r = await refreshAuthCookie();
      authLog("sync:after401Refresh", {
        attempt,
        refreshOk: r.ok,
        needsVerification: !!r.needsVerification,
      });
      if (!shouldApply()) return;
      if (r.needsVerification) {
        setCurrentUser(null);
        return;
      }
      if (r.ok) {
        await wait(80);
        continue;
      }
      await wait(SESSION_SYNC_BASE_DELAY_MS + attempt * 150);
      continue;
    }

    authLog("sync:checkOtherBackoff", {
      attempt,
      status: response.status,
      err: data.error || "",
    });
    await wait(SESSION_SYNC_BASE_DELAY_MS + attempt * 100);
  }

  if (!shouldApply()) return;

  authLog("sync:giveUpLastChance", { attempts: SESSION_SYNC_MAX_ATTEMPTS });
  const lastRefresh = await refreshAuthCookie();
  if (!shouldApply()) return;
  if (lastRefresh.ok) {
    await wait(200);
    if (!shouldApply()) return;
    const lastResponse = await fetchCheck();
    const lastData = await parseJsonSafe(lastResponse);
    if (lastData.success && lastData.authenticated) {
      authLog("sync:checkOkAfterGiveUpRefresh");
      if (!shouldApply()) return;
      setCurrentUser(user);
      return;
    }
    authLog("sync:giveUpLastCheckFailed", { status: lastResponse.status });
  }

  if (!shouldApply()) return;
  authLog("sync:giveUp", { attempts: SESSION_SYNC_MAX_ATTEMPTS });
  setCurrentUser(null);
}

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const refreshIntervalRef = useRef(null);
  const syncGenerationRef = useRef(0);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const myGeneration = ++syncGenerationRef.current;
      const shouldApply = createSyncGuard(syncGenerationRef, myGeneration);
      try {
        authLog("onAuthStateChanged", {
          hasUser: !!user,
          emailVerified: user?.emailVerified ?? null,
          uidTail: user?.uid?.slice(-8) ?? null,
          syncGen: myGeneration,
        });
        await syncServerSession(user, setCurrentUser, shouldApply);
      } catch (err) {
        console.error("Auth state change error:", err);
        if (shouldApply()) {
          setError(err.message);
          setCurrentUser(null);
        }
      } finally {
        setLoading(false);
      }
    });

    return () => {
      syncGenerationRef.current += 1;
      unsubscribe();
    };
  }, []);

  // Proactive refresh and focus refresh when user is set
  useEffect(() => {
    if (!currentUser) {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      return;
    }

    const onFocus = () => {
      refreshAuthCookie();
    };

    refreshIntervalRef.current = setInterval(() => {
      refreshAuthCookie();
    }, PROACTIVE_REFRESH_INTERVAL_MS);

    window.addEventListener("focus", onFocus);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      window.removeEventListener("focus", onFocus);
    };
  }, [currentUser]);

  const value = {
    currentUser,
    loading,
    error,
    setCurrentUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
